import React, {useEffect, useRef, useState} from "react";
import {HiChevronLeft, HiChevronRight} from "react-icons/hi";
import {useDispatch, useSelector} from 'react-redux';
import {ENTITY, getLocalStorageItem, setActiveTab} from '../../redux/slices/timeline';
import TruncatedText from '../../components/common/TruncatedText';
import {
  ConfirmationDialog,
  DependencyCard,
  DependencyForm,
  SearchBar,
  useDependencyManagement,
  useTaskDependencyManagement
} from './components';
import {useMilestoneManagement} from './components/hooks/useMilestoneManagement';
import MilestonesPanel from './components/MilestonesPanel';
import ProjectItem from "./components/ProjectItem";
import EpicDependencyTab from "../../features/EpicSidePanel/components/EpicSidePanelBody/components/EpicDependencyTab/EpicDependencyTab";
import { getEntityTypeDisplay } from "../../utils/entityDisplayUtils";
import { GENERAL_TYPES } from "../../constants/constants";
import { getEntityData } from "../../hooks/RoadmapProjects/getEntityData";
import TaskDependencyTab from "../../features/TaskSidePanel/components/TaskSidePanelBody/components/EpicDependencyTab/TaskDependencyTab";

// Skeleton Components with global colors
const ProjectItemSkeleton = () => (
  <li className="flex items-center px-3 py-3.5 my-2 rounded-lg cursor-pointer hover:bg-light-interactive-hover dark:hover:bg-hover-dark transition-all duration-200 mb-1 border-2 border-light-border-default dark:border-slate-700 shadow-sm hover:border-light-border-interactive dark:hover:border-transparent animate-pulse">
    {/* Status dot skeleton - matches w-2 h-2 rounded-full mr-3 transition-colors duration-200 */}
    <span className="w-2 h-2 rounded-full mr-3 bg-light-surfaceSecondary dark:bg-gray-600 transition-colors duration-200" />

    {/* Lock icon skeleton (conditional space) - matches FiLock text-red-400 mr-2 transition-colors duration-200 */}

    {/* Item name skeleton - matches flex-1 truncate text-base font-medium cursor-pointer transition-colors duration-200 */}
    <span className="flex-1 truncate text-base font-medium cursor-pointer transition-colors duration-200">
      <div className="h-4 bg-light-surfaceSecondary dark:bg-gray-600 rounded w-3/4" />
    </span>

    {/* Timeline icon skeleton - matches ml-2 HiOutlineChartBar size={18} cursor-pointer transition-colors duration-200 */}
    <span className="ml-2 cursor-pointer transition-colors duration-200">
      <div className="w-[18px] h-[18px] bg-light-surfaceSecondary dark:bg-gray-600 rounded" />
    </span>
  </li>
);

const TaskItemSkeleton = () => (
  <li className="flex items-center px-3 py-3.5 my-2 rounded-lg cursor-pointer hover:bg-light-interactive-hover dark:hover:bg-hover-dark transition-all duration-200 mb-1 border-2 border-light-border-default dark:border-slate-700 shadow-sm hover:border-light-border-interactive dark:hover:border-transparent animate-pulse">
    {/* Status dot skeleton - matches w-2 h-2 rounded-full mr-3 transition-colors duration-200 */}
    <span className="w-2 h-2 rounded-full mr-3 bg-light-surfaceSecondary dark:bg-gray-600 transition-colors duration-200" />

    {/* Lock icon skeleton (conditional space) - matches FiLock text-red-400 mr-2 transition-colors duration-200 */}


    {/* Item name skeleton - matches flex-1 truncate text-base font-medium cursor-pointer transition-colors duration-200 */}
    <span className="flex-1 truncate text-base font-medium cursor-pointer transition-colors duration-200">
      <div className="h-4 bg-light-surfaceSecondary dark:bg-gray-600 rounded w-3/4" />
    </span>

    {/* Timeline icon skeleton - matches ml-2 HiOutlineChartBar size={18} cursor-pointer transition-colors duration-200 */}

  </li>
);

const DependencyCardSkeleton = () => (
  <div className="mb-4 rounded-xl border border-light-border-default dark:border-slate-700 bg-light-surfacePrimary dark:bg-secondary-dark shadow-sm p-0 overflow-hidden transition-all duration-300 animate-pulse">
    {/* Header: Name and expand/collapse arrow - matches px-4 pt-3 pb-1 */}
    <div className="flex items-center px-4 pt-3 pb-1">
      {/* Status dot skeleton - matches w-5 text-base */}
      <span className="w-5 text-base mr-3 flex-shrink-0 text-center">
        <div className="w-2 h-2 bg-light-surfaceSecondary dark:bg-gray-600 rounded-full mx-auto" />
      </span>
      {/* Name skeleton - matches flex-1 truncate font-semibold text-base */}
      <div className="flex-1 truncate">
        <div className="h-4 bg-light-surfaceSecondary dark:bg-gray-600 rounded w-2/3" />
      </div>
      {/* Expand arrow skeleton - matches ml-2 size={18} */}
      <div className="ml-2 w-[18px] h-[18px] bg-light-surfaceSecondary dark:bg-gray-600 rounded" />
    </div>

    {/* Status line: Lock, dependency badge, and action icons - matches px-4 pb-2 */}
    <div className="flex items-center gap-2 px-4 pb-2">
      {/* Lock icon skeleton */}
      <div className="w-4 h-4 bg-light-surfaceSecondary dark:bg-gray-600 rounded" />
      {/* Dependency badge skeleton - matches px-2 py-1 text-xs rounded-full */}
      <div className="px-2 py-1 bg-light-surfaceSecondary dark:bg-gray-700 rounded-full">
        <div className="h-3 bg-light-surfaceActive dark:bg-gray-700 rounded w-16" />
      </div>

      {/* Action icons skeleton - matches ml-auto flex items-center gap-2 */}
      <div className="ml-auto flex items-center gap-2">
        {/* Edit icon skeleton - matches p-1.5 rounded-md */}
        <div className="p-1.5 rounded-md">
          <div className="w-[14px] h-[14px] bg-light-surfaceSecondary dark:bg-gray-600 rounded" />
        </div>
        {/* Delete icon skeleton - matches p-1.5 rounded-md */}
        <div className="p-1.5 rounded-md">
          <div className="w-[14px] h-[14px] bg-light-surfaceSecondary dark:bg-gray-600 rounded" />
        </div>
      </div>
    </div>
  </div>
);

// Simple skeleton list with fixed count
const SkeletonList = ({
  type = 'project',
}: {
  type?: 'project' | 'task' | 'dependency';
  containerRef?: React.RefObject<HTMLDivElement>;
}) => {
  const skeletonCount = type === 'dependency' ? 4 : 7;

  const SkeletonComponent = type === 'dependency' ? DependencyCardSkeleton :
    type === 'task' ? TaskItemSkeleton : ProjectItemSkeleton;

  return (
    <div className={`${type === 'dependency' ? 'px-3 mt-1 py-3' : type === 'task' ? 'mt-2' : ''}`}>
      {type === 'project' ? (
        <ul>
          {Array.from({ length: skeletonCount }, (_, index) => (
            <SkeletonComponent key={index} />
          ))}
        </ul>
      ) : type === 'task' ? (
        <div>
          {Array.from({ length: skeletonCount }, (_, index) => (
            <SkeletonComponent key={index} />
          ))}
        </div>
      ) : (
        <div>
          {Array.from({ length: skeletonCount }, (_, index) => (
            <SkeletonComponent key={index} />
          ))}
        </div>
      )}
    </div>
  );
};

const RoadmapSidebar = ({
  childEntityType = "Work Items",
  projects,
  currentRoadmapId,
  collapsed,
  setCollapsed,
  onEntityClick,
  onTimelineClick,
  readOnly = false
} : {
  projects: any[];
  currentRoadmapId: string;
  childEntityType: string;
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  onEntityClick: (id: string) => void;
  onTimelineClick: (projectId: string) => void;
  readOnly?: boolean;
}) => {

  const entityData = getEntityData();


  const dispatch = useDispatch();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [taskStatusFilters, setTaskStatusFilters] = useState<string[]>([]);
  const [taskStatusDropdownOpen, setTaskStatusDropdownOpen] = useState(false);
  const [search, setSearch] = useState<string>("");
  const [depSearch, setDepSearch] = useState('');
  const [taskSearch, setTaskSearch] = useState('');

  // Tab animation state
  const mainTabRef = useRef<HTMLButtonElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({
    width: 0,
    left: 0,
    transition: 'all 0.3s ease'
  });
  const [tabSliderWidth, setTabSliderWidth] = useState<number>(0);

  const {
    data: tasks,
    singleRoadmapLoading,
    loading: isTasksLoading
  } = useSelector((state: any) => state.roadmap);

  const {
    currentEntity,
    currentEntityId,
    simulation,
    projectId,
    taskId,
    activeTab
  } = useSelector((state: any) => state.timeline);
  const { template_id } = useSelector((state: any) => state.templates);
  const isTemplate = !!template_id;

  const singleRoadmapRes = useSelector((state: any) => state.roadmap.singleRoadmapRes);

  // Set initial indicator position based on activeTab
  useEffect(() => {
    updateIndicator();
    updateTabSliderWidth();
  }, [activeTab, childEntityType]);

  // Update indicator position when window resizes
  useEffect(() => {
    const handleResize = () => updateIndicator();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [activeTab, childEntityType]);

  // Update indicator position based on selected tab
  const updateIndicator = () => {
    const activeButton = mainTabRef.current;

    if (activeButton) {
      // Create a temporary span to measure text width
      const tempSpan = document.createElement('span');
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.position = 'absolute';
      tempSpan.style.whiteSpace = 'nowrap';
      tempSpan.style.fontSize = '16px'; // text-base
      tempSpan.style.fontWeight = '600'; // font-semibold
      tempSpan.style.fontFamily = getComputedStyle(activeButton).fontFamily;
      tempSpan.textContent = activeButton.textContent;

      document.body.appendChild(tempSpan);
      const textWidth = tempSpan.offsetWidth;
      document.body.removeChild(tempSpan);

      // Add horizontal padding to the text width (12px padding on each side)
      const dynamicWidth = textWidth + 24; // 12px padding on each side

      // Calculate centered position
      const buttonCenter = activeButton.offsetLeft + (activeButton.offsetWidth / 2);
      const indicatorLeft = buttonCenter - (dynamicWidth / 2);

      setIndicatorStyle({
        width: dynamicWidth,
        left: indicatorLeft,
        transition: 'all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)'
      });
    }
  };

  const updateTabSliderWidth = () => {
    const activeButton = mainTabRef.current;
    if (activeButton) {
      const tempSpan = document.createElement('span');
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.position = 'absolute';
      tempSpan.style.whiteSpace = 'nowrap';
      tempSpan.style.fontSize = '16px';
      tempSpan.style.fontWeight = '600';
      tempSpan.style.fontFamily = getComputedStyle(activeButton).fontFamily;
      tempSpan.textContent = activeButton.textContent;
      document.body.appendChild(tempSpan);
      const textWidth = tempSpan.offsetWidth;
      document.body.removeChild(tempSpan);
      const dynamicWidth = textWidth + 24;
      setTabSliderWidth(dynamicWidth);
    }
  };


  // Use the dependency management hook
  const dependencyManagement = useDependencyManagement({
    projects,
    currentRoadmapId,
    singleRoadmapRes,
    simulation
  });

  // Use the task dependency management hook
  const taskDependencyManagement = useTaskDependencyManagement({
    tasks,
    currentProjectId: currentEntity === ENTITY.PROJECT ? currentEntityId : '', // This should be the current project ID when viewing tasks
    simulation
  });

  const milestoneManagement = useMilestoneManagement({
    currentRoadmapId,
    singleRoadmapRes,
    projects,
  });

  console.log("Project Info", singleRoadmapRes, projects)


  const orderedTabs: Array<'projects' | 'tasks' | 'milestones' | 'dependencies'> = (
    currentEntity === ENTITY.ROADMAP
      ? ['projects', 'milestones', 'dependencies']
      : ['tasks', 'dependencies']
  );
   useEffect(() => {
    const currentTab = activeTab ?? orderedTabs[0];

    const validTab = orderedTabs.includes(currentTab as any) ? currentTab : orderedTabs[0];


      dispatch(setActiveTab(validTab as any));
    
    
    
  }, [currentEntity]);



  // Add confirmation dialog state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    show: boolean;
    type: 'single' | 'bulk';
    projectId: string;
    dependencyId: string;
    projectName: string;
    dependencyName: string;
    position?: { top: number; left: number };
  }>({
    show: false,
    type: 'single',
    projectId: '',
    dependencyId: '',
    projectName: '',
    dependencyName: ''
  });

  // Filtered and searched projects
  const filteredProjects = projects.filter((project: any) => {
    const status = (project?.status || '').toLowerCase();
    if (statusFilters.length > 0 && !statusFilters.includes(status)) return false;
    if (search) {
      const s = search.toLowerCase();
      const id = (project.project_id || project.id || '').toString().toLowerCase();
      const name = (project.project_name || '').toLowerCase();
      
      const entityInfo = getEntityTypeDisplay(project.entity_type, project.type);
      const entityType =
        GENERAL_TYPES?.[
          entityInfo.type as keyof typeof GENERAL_TYPES
        ]?.toLowerCase() || entityInfo.type;

      // Use exact match for ID search as requested
      if (!name.includes(s) && id !== s && entityType !== s) return false;
    }
    return true;
  });

  const projectStatusCounts: Record<string, number> = React.useMemo(() => {
    const counts: Record<string, number> = {};
    const s = (search || '').toLowerCase();
    projects.forEach((project: any) => {
      const matchesSearch = !s || (project.project_name || '').toLowerCase().includes(s);
      if (!matchesSearch) return;
      const status = (project?.status || '').toLowerCase();
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [projects, search]);

  const taskStatusCounts: Record<string, number> = React.useMemo(() => {
    const counts: Record<string, number> = {};
    const s = (taskSearch || '').toLowerCase();
    const list = (tasks && tasks.length >= 1) ? tasks.slice(1) : [];
    list.forEach((task: any) => {
      const matchesSearch = !s ||
        (task.task_title && task.task_title.toLowerCase().includes(s)) ||
        (task.name && task.name.toLowerCase().includes(s));
      if (!matchesSearch) return;
      const st = (task?.status || '').toLowerCase();
      counts[st] = (counts[st] || 0) + 1;
    });
    return counts;
  }, [tasks, taskSearch]);

  // Handle delete dependency confirmation
  const handleDeleteSingleDependencyWithConfirmation = (projectId: string, dependencyId: string, event: React.MouseEvent) => {
    const project = projects.find(p => p.project_id === projectId);
    const dependency = projects.find(p => p.project_id === dependencyId);

    const rect = (event.target as HTMLElement).getBoundingClientRect();

    setDeleteConfirmation({
      show: true,
      type: 'single',
      projectId,
      dependencyId,
      projectName: project?.project_name || 'Unknown Project',
      dependencyName: dependency?.project_name || 'Unknown Dependency',
      position: {
        top: rect.top,
        left: rect.left - 200
      }
    });
  };

  const [milestoneDeleteConfirmation, setMilestoneDeleteConfirmation] = useState<{
    show: boolean;
    uid: string;
    name: string;
    position?: { top: number; left: number };
  }>({ show: false, uid: '', name: '' });

  const handleDeleteMilestoneWithConfirmation = (uid: string, name: string, event: React.MouseEvent) => {
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    setMilestoneDeleteConfirmation({
      show: true,
      uid,
      name,
      position: { top: rect.top, left: rect.left - 200 }
    });
  };

  // Handle delete task dependency confirmation
  const handleDeleteSingleTaskDependencyWithConfirmation = (taskId: string, dependencyId: string, event: React.MouseEvent) => {
    const task = tasks.slice(1).find((t: any) => t.id === taskId);
    const dependency = tasks.slice(1).find((t: any) => t.id === dependencyId);

    const rect = (event.target as HTMLElement).getBoundingClientRect();

    setDeleteConfirmation({
      show: true,
      type: 'single',
      projectId: taskId,
      dependencyId,
      projectName: task?.task_title || task?.name || 'Unknown Task',
      dependencyName: dependency?.task_title || dependency?.name || 'Unknown Task',
      position: {
        top: rect.top,
        left: rect.left - 200
      }
    });
  };

  // Handle bulk delete confirmation
  const handleDeleteAllDependenciesWithConfirmation = (projectId: string, event: React.MouseEvent) => {
    const project = projects.find(p => p.project_id === projectId);
    const depCount = dependencyManagement.dependenciesState.find(dep => dep.project_id === projectId)?.dependencies.length || 0;

    const rect = (event.target as HTMLElement).getBoundingClientRect();

    setDeleteConfirmation({
      show: true,
      type: 'bulk',
      projectId,
      dependencyId: '',
      projectName: project?.project_name || 'Unknown Project',
      dependencyName: `${depCount} dependencies`,
      position: {
        top: rect.top,
        left: rect.left - 200
      }
    });
  };

  // Handle bulk delete task dependencies confirmation
  const handleDeleteAllTaskDependenciesWithConfirmation = (taskId: string, event: React.MouseEvent) => {
    const task = tasks.slice(1).find((t: any) => t.id === taskId);
    const depCount = taskDependencyManagement.taskDependenciesState.find(dep => dep.task_id === taskId)?.dependencies.length || 0;

    const rect = (event.target as HTMLElement).getBoundingClientRect();

    setDeleteConfirmation({
      show: true,
      type: 'bulk',
      projectId: taskId,
      dependencyId: '',
      projectName: task?.task_title || task?.name || 'Unknown Task',
      dependencyName: `${depCount} dependencies`,
      position: {
        top: rect.top,
        left: rect.left - 200
      }
    });
  };

  // Confirm delete action
  const confirmDeleteDependency = async () => {
    // Check if we're dealing with task dependencies or project dependencies
    const isTaskDependency = currentEntity === ENTITY.PROJECT && activeTab === 'dependencies';

    // Store the deletion parameters before clearing the state
    const { projectId, dependencyId, type } = deleteConfirmation;

    // Hide the dialog immediately after confirmation
    setDeleteConfirmation({
      show: false,
      type: 'single',
      projectId: '',
      dependencyId: '',
      projectName: '',
      dependencyName: ''
    });

    try {
      // Perform the deletion operation in the background
      if (type === 'single') {
        if (isTaskDependency) {
          await taskDependencyManagement.handleDeleteSingleTaskDependency(projectId, dependencyId);
        } else {
          await dependencyManagement.handleDeleteSingleDependency(projectId, dependencyId);
        }
      } else {
        if (isTaskDependency) {
          await taskDependencyManagement.handleDeleteAllTaskDependencies(projectId);
        } else {
          await dependencyManagement.handleDeleteAllDependencies(projectId);
        }
      }
    } catch (error) {
      console.error('Error deleting dependency:', error);
      // Could show a toast notification here if needed
    }
  };

  // Cancel delete action
  const cancelDeleteDependency = () => {
    setDeleteConfirmation({
      show: false,
      type: 'single',
      projectId: '',
      dependencyId: '',
      projectName: '',
      dependencyName: ''
    });
  };


  const handleTabUpdate = (tab: string)=>{
    
    dispatch(setActiveTab(tab as any));
    
  }

  return (
    <div
      id="roadmap-sidebar"
      className={`transition-all border-t rounded-r-lg dark:border-gray-700 duration-200 h-full bg-light-surfacePrimary dark:bg-secondary-dark text-light-text-primary dark:text-white border-r border-light-border-default shadow-lg flex flex-col relative w-full`}
    >
      {/* Tab Bar */}
      {!collapsed && (
        <div className="flex items-center justify-between px-3 py-2 border-b rounded-md border-light-border-default dark:border-gray-700">
          <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
            {/* Adaptive tabs like Chrome - active expands, others collapse, fixed order */}
            <div className="flex items-center gap-1 flex-1 transition-all duration-300 overflow-hidden">
              {(() => {
                const isRoadmap = currentEntity === ENTITY.ROADMAP;
                const tabs = isRoadmap
                  ? ["projects", "dependencies", "milestones"]
                  : ["tasks", "dependencies"];
                const labels: Record<string, string> = {
                  projects: "Work Items",
                  tasks: "Work Items",
                  milestones: "Milestones",
                  dependencies: "Dependencies",
                } as any;

                const getBasis = (tab: string) => {
                  const isActive =
                    tab === activeTab ||
                    (!activeTab && tab === (isRoadmap ? "projects" : "tasks"));
                  // Active grows; others shrink and truncate
                  return isActive ? "60%" : isRoadmap ? "20%" : "40%";
                };

                console.log("activeTab", activeTab);
                return tabs.map((tab, index) => {
                  const isActive =
                    (activeTab || (isRoadmap ? "projects" : "tasks")) === tab;
                  return (
                    <button
                      key={tab}
                      className={`side-panel-tab ${isActive ? "side-panel-tab--active" : "side-panel-tab--inactive"}`}
                      style={{
                        flexBasis: getBasis(tab),
                        flexGrow: 0,
                        flexShrink: 1,
                        minWidth: "0",
                      }}
                      onClick={() => handleTabUpdate(tab)}
                    >
                      <TruncatedText
                        text={labels[tab]}
                        className="side-panel-tab__text text-xs"
                        placement="top"
                      />
                    </button>
                  );
                });
              })()}
            </div>
          </div>
          <button
            className="text-light-text-muted hover:text-light-text-primary dark:text-gray-400 dark:hover:text-white transition-colors duration-200"
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <HiChevronRight size={20} />
            ) : (
              <HiChevronLeft size={20} />
            )}
          </button>
        </div>
      )}

      {collapsed && (
        <div className="flex items-center justify-center pt-3 pb-1">
          <button
            className="text-light-text-muted hover:text-light-text-primary dark:text-gray-400 dark:hover:text-white transition-colors duration-200"
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <HiChevronRight size={20} />
            ) : (
              <HiChevronLeft size={20} />
            )}
          </button>
        </div>
      )}

      {/* Tab Content */}
      <div
        className={`flex-1 transition-all duration-300 ${collapsed ? "hidden" : "overflow-y-auto"} relative`}
      >
        {!collapsed &&
          currentEntity === ENTITY.ROADMAP &&
          activeTab === "projects" && (
            <>
              {/* Search Bar with Status Filter */}
              <div className="sticky top-0 bg-light-surfacePrimary dark:bg-secondary-dark z-10 px-2">
                <SearchBar
                  className=" "
                  searchValue={search}
                  onSearchChange={setSearch}
                  placeholder={`Search for Work Items`}
                  statusFilters={statusFilters}
                  setStatusFilters={setStatusFilters}
                  statusDropdownOpen={statusDropdownOpen}
                  setStatusDropdownOpen={setStatusDropdownOpen}
                  statusCounts={projectStatusCounts}
                  showStatusFilter={!isTemplate}
                />
              </div>

              {/* List of Projects */}
              <div
                className={`flex-1 overflow-y-auto  ${collapsed ? "" : "px-2 py-2"} transition-all duration-300`}
              >
                {singleRoadmapLoading ? (
                  <SkeletonList type="project" />
                ) : (
                  <ul className="space-y-2">
                    {filteredProjects.map((project: any) => (
                      <ProjectItem
                        key={project.project_id}
                        id={project.project_id}
                        simFlag={simulation}
                        name={project.project_name}
                        status={project?.status}
                        simStatus={project?.sim_data?.status}
                        dependencies={project.dependencies}
                        isHighlighted={
                          project.project_id ===
                          projectId?.replace("project-", "")
                        }
                        tooltipType="project"
                        projectData={project}
                        isTemplate={isTemplate}
                        onItemClick={() =>
                          onEntityClick(`project-${project.project_id}`)
                        }
                        onTimelineClick={async () => {
                          dispatch(setActiveTab("tasks"));
                          onTimelineClick(project.project_id);
                        }}
                      />
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}

        {!collapsed &&
          currentEntity === ENTITY.ROADMAP &&
          activeTab === "dependencies" && (
            <EpicDependencyTab
              dependencyManagement={dependencyManagement}
              projects={projects} // Passing dependenciesState as projects for search/delete logic inside
              readOnly={readOnly}
              simulation={simulation}
              childEntityType={childEntityType}
              singleRoadmapLoading={singleRoadmapLoading}
              onEntityClick={onEntityClick}
            />
          )}

        {/* Milestones Tab (Roadmap-level) */}
        {!collapsed &&
          currentEntity === ENTITY.ROADMAP &&
          activeTab === "milestones" && (
            <MilestonesPanel
              milestoneManagement={milestoneManagement}
              singleRoadmapLoading={singleRoadmapLoading}
              minDate={entityData?.start_date}
              maxDate={entityData?.end_date}
            />
          )}

        {!collapsed &&
          currentEntity === ENTITY.PROJECT &&
          activeTab !== "dependencies" && (
            <>
              {/* Task search bar */}
              <div className=" sticky top-0 bg-light-surfacePrimary dark:bg-secondary-dark z-10 py-1 px-2">
                <SearchBar
                  searchValue={taskSearch}
                  onSearchChange={setTaskSearch}
                  placeholder={`Search for ${childEntityType.toLowerCase()}`}
                  statusFilters={taskStatusFilters}
                  setStatusFilters={setTaskStatusFilters}
                  statusDropdownOpen={taskStatusDropdownOpen}
                  setStatusDropdownOpen={setTaskStatusDropdownOpen}
                  showStatusFilter={!isTemplate}
                  filterType="task"
                  statusCounts={taskStatusCounts}
                />
              </div>

              <div className="px-3">
                {isTasksLoading ? (
                  <SkeletonList type="task" />
                ) : (
                  <div className="space-y-2 mt-2">
                    {tasks && tasks.length >= 1 ? (
                      tasks
                        .slice(1)
                        .filter((task: any) => {
                          // Search filter
                          const search = taskSearch.toLowerCase();
                          const id = (
                            task.id ||
                            task.task_id ||
                            task.item_id ||
                            ""
                          )
                            .toString()
                            .toLowerCase();
                          const matchesSearch =
                            (task.task_title &&
                              task.task_title.toLowerCase().includes(search)) ||
                            (task.name &&
                              task.name.toLowerCase().includes(search)) ||
                            id === search;

                          const entityInfo = getEntityTypeDisplay(
                            task.entity_type,
                            task.type,
                          );
                          const entityType =
                            GENERAL_TYPES?.[
                              entityInfo.type as keyof typeof GENERAL_TYPES
                            ]?.toLowerCase() || entityInfo.type;

                          const matchesEntityType = entityType === search;

                          const finalMatch = matchesSearch || matchesEntityType;

                          // Status filter - skip for templates
                          if (isTemplate) {
                            return finalMatch;
                          }
                          const status = (task?.status || "").toLowerCase();
                          const matchesStatus =
                            taskStatusFilters.length === 0 ||
                            taskStatusFilters.includes(status);

                          return finalMatch && matchesStatus;
                        })
                        .map((task: any) => (
                          <ProjectItem
                            key={task.item_id}
                            id={task.id}
                            simFlag={simulation}
                            name={task.task_title || task.name}
                            status={task.status}
                            dependencies={task.dependencies}
                            isHighlighted={
                              task.id === taskId?.split("__").at(-1)
                            }
                            showTimelineIcon={false}
                            tooltipType="task"
                            taskData={task}
                            isTemplate={isTemplate}
                            onItemClick={() =>
                              onEntityClick(
                                `task__${currentEntityId}__${task.id}`,
                              )
                            }
                          />
                        ))
                    ) : (
                      <div className="text-sm text-light-text-muted dark:text-gray-400 text-center py-4">
                        No {childEntityType.toLowerCase()} found for this{" "}
                        {currentEntity === ENTITY.ROADMAP ? "project" : "task"}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

        {!collapsed &&
          currentEntity === ENTITY.PROJECT &&
          activeTab === "dependencies" && (
            <TaskDependencyTab
              simFlag={simulation}
              readOnly={readOnly}
              onEntityClick={onEntityClick}
              currentEntityId={currentEntityId}
              childEntityType={childEntityType}
              taskDependencyManagement={taskDependencyManagement}
              setExpandedId={setExpandedId}
              expandedId={expandedId}
              depSearch={depSearch}
              setDepSearch={setDepSearch}
              handleDeleteAllTaskDependenciesWithConfirmation={
                handleDeleteAllTaskDependenciesWithConfirmation
              }
              handleDeleteSingleTaskDependencyWithConfirmation={
                handleDeleteSingleTaskDependencyWithConfirmation
              }
              tasks={tasks}
            ></TaskDependencyTab>
          )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteConfirmation.show}
        type={deleteConfirmation.type}
        position={deleteConfirmation.position}
        dependencyName={deleteConfirmation.dependencyName}
        entityLabel={"dependency"}
        isLoading={
          currentEntity === ENTITY.PROJECT && activeTab === "dependencies"
            ? taskDependencyManagement.isTaskActionLoading
            : dependencyManagement.isActionLoading
        }
        onConfirm={readOnly ? () => {} : confirmDeleteDependency}
        onCancel={cancelDeleteDependency}
      />
      <ConfirmationDialog
        isOpen={milestoneDeleteConfirmation.show}
        type={"single"}
        position={milestoneDeleteConfirmation.position}
        dependencyName={milestoneDeleteConfirmation.name}
        entityLabel={"milestone"}
        isLoading={milestoneManagement.isMilestoneActionLoading}
        onConfirm={async () => {
          const uid = milestoneDeleteConfirmation.uid;
          setMilestoneDeleteConfirmation({ show: false, uid: "", name: "" });
          await milestoneManagement.handleDeleteMilestone(uid);
        }}
        onCancel={() =>
          setMilestoneDeleteConfirmation({ show: false, uid: "", name: "" })
        }
      />
    </div>
  );
};

export default RoadmapSidebar;

// Inject fade-in keyframes only once
if (typeof window !== 'undefined' && !document.getElementById('roadmap-sidebar-fadein-style')) {
  const style = document.createElement('style');
  style.id = 'roadmap-sidebar-fadein-style';
  style.innerHTML = `
    @keyframes fade-in { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; } }
    .animate-fade-in { animation: fade-in 0.3s cubic-bezier(0.4,0,0.2,1); }
  `;
  document.head.appendChild(style);
} 